import { FunctionComponent, useContext, useEffect, useCallback } from "react";
import { ApplicationContext } from "../../components/ApplicationProvider";
import { ListPrinterTable } from "./ListPrinterTable";
import {
    PurpleButton,
    FooterButtonContainer,
    CircularProgressIndicators,
    ConfigureAnalyzeSaasPageCard,
    NoPrinterMessage,
} from "./AnalyzeStyle";
import { CircularSegmentElement } from "./CircularSegmentElement";
import { AnalyzeDeploymentType } from "./AnalyzeSelection";
import { useMutation } from "@apollo/client";
import { Loader, Notifier } from "grabcad-ui-elements";
import { UserContext } from "../../components/User/UserProvider";
import { UPDATE_COMPANY_ANALYZE_DEPLOYMENT_TYPE } from "../../graphql/Mutations/Companies";
import { GET_COMPANY_AS_ADMIN, useCompanyAsAdminQuery } from "../../graphql";
import { useGetAllSaaSPrinters } from "./UseGetAllSaaSPrinters";
import { Dimmer } from "semantic-ui-react";
import { ConfigureAnalyzeSassPrintersList } from "./AnalyzeInterface";
import { config } from "../../config";
import { fullAnalyzeUrl, PATHS } from "../../Routes";
import { useHistory } from "react-router-dom";
import { CREATE_EVENT } from "../../graphql/Mutations/Event";
import {
    EventCategory,
    METRIC_EVENT_CATALOG,
    MetricEventKey,
} from "@grabcad/company-server-shared/dist/types";
import { isEmbeddedInGrabCADPrint } from "../../utils/isGrabCADPrint";
import { broadcastAnalyzeUrlLaunchToGrabCADPrint } from "../../utils/grabcadPrintContextBridge";
export const ConfigureAnalyzeSaaS: FunctionComponent<{}> = () => {
    const { t } = useContext(ApplicationContext);
    const { isCompanyAdmin } = useContext(UserContext);
    const history = useHistory();
    const { companyLoading, company } = useCompanyAsAdminQuery();
    const [triggerCreateEvent] = useMutation(CREATE_EVENT);

    const {
        loadingData,
        listOfPrinters,
        gatewayPrinterCount,
        printServerPrinterCount,
        streamlineProLicenceCount,
    } = useGetAllSaaSPrinters({
        includePrinterCount: true,
        includeGatewayCount: true,
        includePrintServerCount: true,
        includeStreamlineCount: true,
    });

    const [updateChoice] = useMutation(UPDATE_COMPANY_ANALYZE_DEPLOYMENT_TYPE, {
        refetchQueries: [
            {
                query: GET_COMPANY_AS_ADMIN,
                variables: { id: company?.id },
            },
        ],
        onError: (error) => Notifier.error(error?.message),
    });

    const displayCircularIndicatorElements = () => {
        return [
            {
                total: gatewayPrinterCount || 0,
                element: "IoT Gateway",
            },
            {
                total: printServerPrinterCount || 0,
                element: "Print Server",
            },
        ].map((element) => {
            return (
                <CircularSegmentElement
                    key={element.element}
                    total={element.total as number}
                    element={element.element}
                />
            );
        });
    };

    const handleNavigation = useCallback(async () => {
        // Fire event for launching Analyze SaaS (non-blocking)
        if (company?.id && !company.internal) {
            const { action, description } =
                METRIC_EVENT_CATALOG[MetricEventKey.ANALYZE_SAAS_LAUNCH];
            void triggerCreateEvent({
                variables: {
                    events: [
                        {
                            event: EventCategory.GC_ANALYZE,
                            action,
                            description,
                        },
                    ],
                    companyId: company.id,
                },
            }).catch((err) => console.error("Failed to log Analyze SaaS launch event:", err));
        }

        const shouldUpdateDeploymentType =
            company?.analyzeDeploymentType !== AnalyzeDeploymentType.saas;

        if (shouldUpdateDeploymentType) {
            if (isEmbeddedInGrabCADPrint) {
                try {
                    await updateChoice({
                        variables: { analyzeDeploymentType: AnalyzeDeploymentType.saas },
                    });
                } catch {
                    // Prevent launch broadcast on embedded flow when deployment type update fails.
                    return;
                }
            } else {
                void updateChoice({
                    variables: { analyzeDeploymentType: AnalyzeDeploymentType.saas },
                });
            }
        }

        if (config.ANALYZE_SAAS_URL) {
            if (!isEmbeddedInGrabCADPrint) {
                window.location.href = fullAnalyzeUrl(config.ANALYZE_SAAS_URL);
                return;
            }
            broadcastAnalyzeUrlLaunchToGrabCADPrint();
        }
    }, [company, triggerCreateEvent, updateChoice]);

    useEffect(() => {
        if (company?.analyzeDeploymentType === AnalyzeDeploymentType.onPrem) {
            history.replace(PATHS.configureAnalyzeOnPrem);
        }
    }, [company, history]);

    if (companyLoading || loadingData) {
        return (
            <Dimmer active inverted>
                <Loader size="large" inline="centered">
                    Loading
                </Loader>
            </Dimmer>
        );
    } else {
        return (
            <ConfigureAnalyzeSaasPageCard>
                <div style={{ display: "flex" }}>
                    <h2 className="page-header">
                        {t("configure_analyze.configure_analyze_sass.configure_analyze_sass")}
                    </h2>
                </div>

                <CircularProgressIndicators>
                    {displayCircularIndicatorElements()}
                </CircularProgressIndicators>

                <ListPrinterTable
                    listOfPrinters={listOfPrinters as ConfigureAnalyzeSassPrintersList[]}
                    streamlineProLicenceCount={streamlineProLicenceCount as number}
                />

                <FooterButtonContainer>
                    <NoPrinterMessage data-testid="no-printer-message">
                        {t("analyze_selection.analyze_printer_info")}
                    </NoPrinterMessage>

                    <PurpleButton
                        onClick={handleNavigation}
                        disabled={Number(streamlineProLicenceCount) <= 0 || !isCompanyAdmin}
                        style={{
                            maxWidth: "200px",
                        }}
                    >
                        {t("configure_analyze.configure_analyze_sass.launch_analyze_saas")}
                    </PurpleButton>
                </FooterButtonContainer>
            </ConfigureAnalyzeSaasPageCard>
        );
    }
};
